import { useContext } from 'react';

import { useUserProfile } from '@atom/hooks/useUserProfile';
import { Button } from '@atom/mui';
import colors from '@atom/styles/colors';
import { ARDOTAttribute, AssetRequestStatus } from '@atom/types/assetRequest';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';
import { getDisplayDateTime } from '@atom/utilities/timeUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import { ardotAttributeDictionaryAssetDetail } from '../../customTenantUtilities';
import ViewRequestContext from '../ViewRequestContext';

import ReceiverSection from './ReceiverSection';
import ReviewerSection from './ReviewerSection';

import classes from './ardotView.module.css';

const styles = {
  cancelButton: {
    width: '100%',
    color: colors.brand.red,
    border: `1px solid ${colors.neutral.ash}`,
    marginTop: '1rem',
  },
};

const ARDOT118TransferView = () => {
  const userProfile = useUserProfile();
  const { activeRequest, fromAsset, setStatus, setConfirmOpen } =
    useContext(ViewRequestContext);
  const attributeDictionary = ardotAttributeDictionaryAssetDetail(fromAsset);

  const createdByText = `Created by ${activeRequest?.createdBy?.firstName} ${
    activeRequest?.createdBy?.lastName
  } on ${getDisplayDateTime(activeRequest?.createdDate)}`;

  const canceledByText = `Canceled by ${activeRequest?.updatedBy?.firstName} ${
    activeRequest?.updatedBy?.lastName
  } on ${getDisplayDateTime(activeRequest?.cancelDate)}`;

  const handleCancel = () => {
    setStatus(AssetRequestStatus.CANCELED);
    setConfirmOpen(true);
  };

  const showCanceledByText =
    activeRequest?.status === AssetRequestStatus.CANCELED;
  const showCancel =
    (userProfile?.userId === activeRequest?.createdBy?.id ||
      hasRolePermissions(ROLE_SETS.ORG_ADMIN)) &&
    (activeRequest?.status === AssetRequestStatus.PENDING_REVIEW ||
      activeRequest?.status === AssetRequestStatus.PENDING_APPROVAL);

  return (
    <>
      <div>
        <div className={classes.rowTitle}>Tag Number</div>
        <div>{attributeDictionary[ARDOTAttribute.TAG_NUMBER]?.value || ''}</div>
      </div>
      <div className={classes.viewRow}>
        <div className={classes.rowTitle}>Description</div>
        <div>
          {attributeDictionary[ARDOTAttribute.DESCRIPTION]?.value || ''}
        </div>
      </div>
      {!isNilOrEmpty(activeRequest?.attributes) && (
        <>
          <div
            className={classes.sectionTitle}
          >{`Update Attributes (${activeRequest?.attributes.length})`}</div>
          <div className={classes.sectionSubtext}>
            Update the inventory attributes once the request is approved.
          </div>
          <>
            {activeRequest?.attributes.map(attribute => (
              <div key={attribute?.id} className={classes.viewRow}>
                <div className={classes.rowTitle}>{attribute?.name}</div>
                <div>
                  <span className={classes.oldValue}>
                    {attribute?.oldValue}
                  </span>
                  <span className={classes.newValue}>
                    {attribute?.newValue}
                  </span>
                </div>
              </div>
            ))}
          </>
        </>
      )}
      {activeRequest?.toCategoryId && (
        <>
          <div className={classes.sectionTitle}>Transfer</div>
          <div className={classes.sectionSubtext}>
            Transfer the inventory to another district or division once the
            request is approved.
          </div>
          <div className={classes.viewRow}>
            <div className={classes.rowTitle}>From District or Division</div>
            <div>{activeRequest?.categoryPath || ''}</div>
          </div>
          <div className={classes.viewRow}>
            <div className={classes.rowTitle}>To District or Division</div>
            <div>{activeRequest?.toCategoryPath || ''}</div>
          </div>
        </>
      )}
      <div className={classes.sectionTitle}>Approval</div>
      <ReviewerSection />
      <ReceiverSection />
      {showCancel && (
        <Button sx={styles.cancelButton} onClick={handleCancel}>
          Cancel Request
        </Button>
      )}
      <div className={classes.createdSubtext}>{createdByText}</div>
      {showCanceledByText && (
        <div className={classes.createdSubtext}>{canceledByText}</div>
      )}
    </>
  );
};

export default ARDOT118TransferView;
